import { Company, NextLevelKey, OrgTreeItem } from '@employ-engage/data';
import isArray from 'lodash.isarray';
import { useEffect, useState, useRef } from 'react';
import AddOrgModal from '../add-org-modal/add-org-modal';
import DeleteOrgModal from '../delete-org-modal/delete-org-modal';
import classNames from 'classnames';
import Tooltip from '../tooltip/tooltip';
import RenameOrgModal from '../rename-org-modal/rename-org-modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import { faCirclePlus } from '@fortawesome/pro-solid-svg-icons';
import {
  findNextTreeLabel,
  findPrevTreeLabel,
  isElementVisible,
} from './org-tree-arrow-nav-utils';

export interface OrgTreeProps {
  isSelectable?: boolean;
  isCollapsible?: boolean;
  isEditable?: boolean;
  isLastItem?: boolean;
  selectedItems?: string[];
  previousDepartmentLevel?: string[];
  handleToggleSelection?: (department: string[]) => void;
  path: (string | number)[]; // path to the current node
  data: OrgTreeItem;
  nextLevelKeys: NextLevelKey[];
  level: number;
  company: Company;
  completeOrgTree: OrgTreeItem;
  collapseOrganization?: boolean;
  onSelectOrganization?: (path: (string | number)[]) => void;
  refetchCompany: () => void;
}

export function OrgTree({
  isSelectable,
  isCollapsible = false,
  isEditable,
  isLastItem,
  selectedItems,
  previousDepartmentLevel = [],
  handleToggleSelection,
  data,
  path,
  nextLevelKeys,
  level,
  company,
  completeOrgTree,
  collapseOrganization = false,
  onSelectOrganization,
  refetchCompany,
}: OrgTreeProps) {
  const { t } = useTranslation(['common']);
  const [openAddOrgModal, setOpenAddOrgModal] = useState<boolean>(false);
  const [openRenameOrgModal, setOpenRenameOrgModal] = useState<boolean>(false);
  const [openDeleteOrgModal, setOpenDeleteOrgModal] = useState<boolean>(false);
  const [collapseTree, setCollapseTree] =
    useState<boolean>(collapseOrganization);
  useEffect(() => {
    setCollapseTree(collapseOrganization);
  }, [collapseOrganization]);

  // Use a ref for the main tree label for keyboard navigation
  const treeLabelRef = useRef<HTMLSpanElement>(null);

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLSpanElement>,
    elementTypeClass: string
  ) => {
    if (e.key === 'ArrowRight') {
      const orgTreeRoot = document.querySelector('.root-of-org-tree');
      const tabbableElements = Array.from(
        orgTreeRoot?.querySelectorAll('[tabindex="0"]') || []
      );
      const currentIndex = tabbableElements.indexOf(e.currentTarget);

      // Move to the next one (wraps around if at the end)
      let nextIndex = (currentIndex + 1) % tabbableElements.length;
      while (
        nextIndex !== currentIndex &&
        !isElementVisible(tabbableElements[nextIndex] as HTMLElement)
      ) {
        nextIndex = (nextIndex + 1) % tabbableElements.length;
      }
      (tabbableElements[nextIndex] as HTMLElement).focus();
      e.preventDefault();
    }

    if (e.key === 'ArrowLeft') {
      const orgTreeRoot = document.querySelector('.root-of-org-tree');
      const tabbableElements = Array.from(
        orgTreeRoot?.querySelectorAll('[tabindex="0"]') || []
      );
      const currentIndex = tabbableElements.indexOf(e.currentTarget);

      // Move to the next one (wraps around if at the end)
      let nextIndex =
        (((currentIndex - 1) % tabbableElements.length) +
          tabbableElements.length) %
        tabbableElements.length;
      while (
        nextIndex !== currentIndex &&
        !isElementVisible(tabbableElements[nextIndex] as HTMLElement)
      ) {
        nextIndex =
          (((nextIndex - 1) % tabbableElements.length) +
            tabbableElements.length) %
          tabbableElements.length;
      }
      (tabbableElements[nextIndex] as HTMLElement).focus();
      e.preventDefault();
    }

    if (e.key === 'ArrowDown') {
      const nextLabel = findNextTreeLabel(
        treeLabelRef.current,
        elementTypeClass
      );
      if (nextLabel) nextLabel.focus();
      e.preventDefault();
    }
    if (e.key === 'ArrowUp') {
      // Prefer deepest last child of previous sibling, then previous sibling, then parent recursively
      const prevLabel = findPrevTreeLabel(
        treeLabelRef.current,
        elementTypeClass
      );
      if (prevLabel) prevLabel.focus();
      e.preventDefault();
    }

    if (
      elementTypeClass === '.org-tree-label' &&
      (e.key === 'Enter' || e.key === ' ')
    ) {
      if (onSelectOrganization) {
        onSelectOrganization(path);
      } else if (isCollapsible) {
        setCollapseTree(!collapseTree);
      } else if (isSelectable) {
        const departmentLevel: string[] =
          previousDepartmentLevel.length > 0
            ? previousDepartmentLevel.concat(data.id)
            : [company.id];
        handleToggleSelection?.(departmentLevel);
      }
      e.preventDefault();
    }
  };

  const isLastLevel = level === nextLevelKeys.length - 1;
  const isFirstLevel = level === 0;
  const color = nextLevelKeys[level].color;
  const departmentLevel: string[] =
    previousDepartmentLevel.length > 0
      ? previousDepartmentLevel.concat(data.id)
      : [company.id];

  const isInputChecked = selectedItems?.includes(departmentLevel.join('.'));

  return (
    <div
      className={classNames(`organization-tree-${level}`, {
        'root-of-org-tree': isFirstLevel,
      })}
    >
      <div className="flex items-center border border-b border-l-0 border-r-0 border-t-0 border-gray-200">
        {isEditable && (
          <div className="flex">
            <div className={!isFirstLevel && isEditable ? '' : 'invisible'}>
              <Tooltip
                label={t('rename-organization', {
                  orgLevelName: t(nextLevelKeys[level]?.name),
                  orgName: isFirstLevel ? company.name : data.name,
                })}
              >
                <button
                  onKeyDown={(e) => handleKeyDown(e, '.org-tree-edit-button')}
                  className="org-tree-edit-button"
                  tabIndex={0}
                  onClick={() => setOpenRenameOrgModal(true)}
                >
                  <i className="fa-solid fa-pen-to-square mr-1 cursor-pointer text-semantic-success" />
                </button>
              </Tooltip>
            </div>
            <div className={!isFirstLevel && isEditable ? '' : 'invisible'}>
              <Tooltip
                label={t('delete-org', {
                  orgLevelName: t(nextLevelKeys[level]?.name),
                  orgName: isFirstLevel ? company.name : data.name,
                })}
              >
                <button
                  onKeyDown={(e) => handleKeyDown(e, '.org-tree-delete-button')}
                  className="org-tree-delete-button"
                  tabIndex={0}
                  onClick={() => setOpenDeleteOrgModal(true)}
                >
                  <i className="fa-solid fa-trash mr-1 cursor-pointer text-semantic-error" />
                </button>
              </Tooltip>
            </div>
          </div>
        )}
        <span style={{ width: level * 16 }}></span>
        {isLastLevel || !isCollapsible ? (
          <i className="w-6 h-6 mr-2" />
        ) : (
          <button
            tabIndex={0}
            type="button"
            onKeyDown={(e) => handleKeyDown(e, '.org-tree-collapse-button')}
            onClick={() => setCollapseTree(!collapseTree)}
            className={classNames('org-tree-collapse-button', {
              'transform rotate-90': !collapseTree,
              'transform rotate-0': collapseTree,
            })}
          >
            <FontAwesomeIcon
              icon="chevron-right"
              style={{ color: typeof color === 'string' ? color : color.hex }}
              className="mr-2"
            />
          </button>
        )}
        {isSelectable && (
          <input
            tabIndex={0}
            type="checkbox"
            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600 mr-2"
            checked={isInputChecked}
            onChange={() => handleToggleSelection?.(departmentLevel)}
          />
        )}
        {/* unicode box character */}
        <span className="mr-1">
          {isLastItem === undefined ? '' : isLastItem ? '\u2517' : '\u2523'}
        </span>
        <span
          className="org-tree-label"
          ref={treeLabelRef}
          tabIndex={0}
          onKeyDown={(e) => handleKeyDown(e, '.org-tree-label')}
          onClick={() =>
            onSelectOrganization
              ? onSelectOrganization(path)
              : isCollapsible
              ? setCollapseTree(!collapseTree)
              : isSelectable
              ? handleToggleSelection?.(departmentLevel)
              : null
          }
          style={{
            color: typeof color === 'string' ? color : color.hex,
            cursor: onSelectOrganization ? 'pointer' : 'default',
          }}
        >
          {t(nextLevelKeys[level].name)}:{' '}
          {isFirstLevel ? company.name : data.name}
        </span>
        <div className={!isLastLevel && isEditable ? '' : 'invisible'}>
          <Tooltip
            label={t(`add-new-level`, {
              level: nextLevelKeys[level + 1]?.name,
            })}
          >
            <button
              tabIndex={0}
              onKeyDown={(e) => handleKeyDown(e, '.org-tree-add-button')}
              className="ml-2 org-tree-add-button"
              onClick={() => setOpenAddOrgModal(true)}
            >
              <FontAwesomeIcon
                icon={faCirclePlus}
                className={classNames(
                  `add-org-icon-${level}`,
                  'cursor-pointer dark:text-semantic-info'
                )}
              />
            </button>
          </Tooltip>
        </div>
      </div>
      <AddOrgModal
        path={path}
        completeOrgTree={completeOrgTree}
        orgType={`${nextLevelKeys[level + 1]?.name}`}
        itemLabel={`${t(nextLevelKeys[level]?.name)}: ${t(
          isFirstLevel ? company.name : data.name
        )}
        `}
        open={openAddOrgModal}
        setOpen={setOpenAddOrgModal}
        nextLevelSubKeys={nextLevelKeys.slice(0, level + 2)}
        setCollapseTree={setCollapseTree}
        refetchCompany={refetchCompany}
        company={company}
      />
      <RenameOrgModal
        itemLabel={`${isFirstLevel ? company.name : data.name}`}
        path={path}
        completeOrgTree={completeOrgTree}
        orgType={t(nextLevelKeys[level]?.name)}
        open={openRenameOrgModal}
        setOpen={setOpenRenameOrgModal}
        refetchCompany={refetchCompany}
      />
      <DeleteOrgModal
        itemLabel={`${isFirstLevel ? company.name : data.name}`}
        path={path}
        completeOrgTree={completeOrgTree}
        orgType={t(nextLevelKeys[level]?.name)}
        open={openDeleteOrgModal}
        setOpen={setOpenDeleteOrgModal}
        refetchCompany={refetchCompany}
      />
      {!collapseTree && (
        <ul>
          {isArray(data[nextLevelKeys[level + 1]?.id]) &&
            (data[nextLevelKeys[level + 1].id] as OrgTreeItem[]).map(
              (item, index, items) => {
                return (
                  <li key={`${item.id}`}>
                    <OrgTree
                      isSelectable={isSelectable}
                      isCollapsible={isCollapsible}
                      isEditable={isEditable}
                      isLastItem={index === items.length - 1}
                      selectedItems={selectedItems}
                      previousDepartmentLevel={departmentLevel}
                      handleToggleSelection={handleToggleSelection}
                      path={[...path, nextLevelKeys[level + 1].id, index]}
                      company={company}
                      completeOrgTree={completeOrgTree}
                      data={item}
                      nextLevelKeys={nextLevelKeys}
                      level={level + 1}
                      collapseOrganization={collapseOrganization}
                      onSelectOrganization={onSelectOrganization}
                      refetchCompany={refetchCompany}
                    />
                  </li>
                );
              }
            )}
        </ul>
      )}
    </div>
  );
}

export default OrgTree;
